import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";

/**
 * Deep-link focus for the Data Flows editing steps. The finished data-map diagram links straight
 * at a single vendor, category or purpose, so the receiving page has to scroll to it and say which
 * one it landed on.
 *
 * Reads `?<param>=<id>` once, drops it from the URL so a refresh doesn't re-trigger, then scrolls
 * `#<elementIdPrefix>-<id>` into view. Returns the id so the caller can render a temporary
 * highlight; it clears itself after a few seconds.
 */
export function useDataFlowsDeepLinkFocus(
  param: "vendor" | "category" | "purpose",
  elementIdPrefix: string,
  isReady: boolean,
): string | null {
  const history = useHistory();
  const location = useLocation();
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [requestedId, setRequestedId] = useState<string | null>(null);

  // Consume the param as soon as it appears, independent of page readiness, so it can't be
  // re-read after we rewrite the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get(param);
    if (!id) {
      return;
    }
    setRequestedId(id);
    // Drop only the param we consumed — anything else on the URL is not ours to discard.
    params.delete(param);
    const remaining = params.toString();
    history.replace(`${location.pathname}${remaining ? `?${remaining}` : ""}${location.hash}`);
  }, [param, history, location.search, location.pathname, location.hash]);

  // Scroll once the page has actually rendered the target. The row can mount well after the
  // page reports itself ready — these steps load a data map, a vendor list and a survey — so
  // poll briefly for the anchor rather than checking once and giving up.
  useEffect(() => {
    if (!requestedId || !isReady) {
      return;
    }
    let attempts = 0;
    const findAndFocus = () => {
      const element = document.getElementById(`${elementIdPrefix}-${requestedId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setFocusedId(requestedId);
        setRequestedId(null);
        return;
      }
      attempts += 1;
      if (attempts < 40) {
        timer = window.setTimeout(findAndFocus, 100);
      } else {
        // Give up quietly: a stale link is not worth an error state.
        setRequestedId(null);
      }
    };
    let timer = window.setTimeout(findAndFocus, 0);
    return () => window.clearTimeout(timer);
  }, [requestedId, isReady, elementIdPrefix]);

  // Let the highlight fade rather than sit on the row for the rest of the session.
  useEffect(() => {
    if (!focusedId) {
      return;
    }
    const timer = window.setTimeout(() => setFocusedId(null), 2600);
    return () => window.clearTimeout(timer);
  }, [focusedId]);

  return focusedId;
}

/** Ring shown on a deep-linked row or card while the focus highlight is active. */
export const DEEP_LINK_FOCUS_STYLE: CSSProperties = {
  boxShadow: "0 0 0 2px #7F77DD",
  borderRadius: 8,
  transition: "box-shadow 200ms ease-out",
};
